import { useEffect, useRef, useState } from 'react';
import { DidYouMean } from './Components/DidYouMean';
import { SearchBox } from './Components/SearchBox';
import FacetBreadcrumbs from './Components/FacetBreadcrumbs';
import Facet from './Components/Facet';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { GridCol, GridRow } from '@lmig/lmds-react-grid';
import ResultsPerPage from './Components/ResultsPerPage';
//import RelevanceInspector from './Components/RelevanceInspector';
import QuerySummary from './Components/QuerySummary';
import Sort from './Components/Sort';
import Pager from './Components/Pager';
import { ResultListFolded } from './Components/ResultListFolded';
import { ResultTemplatesProvider } from './Components/ResultTemplates/ResultTemplates';
import { useCoveoSearchEngine } from './CoveoSearchEngine';
import {
  buildBreadcrumbManager,
  buildDidYouMean,
  buildFacet,
  buildPager,
  buildQuerySummary,
  buildRelevanceSortCriterion,
  buildSearchBox,
  buildSearchStatus,
  buildSort,
  buildResultsPerPage,
  buildFoldedResultList,
  loadSearchAnalyticsActions,
  loadSearchActions,
  SortOrder,
  buildFieldSortCriterion,
  buildNotifyTrigger,
} from '@coveo/headless';
import { getCoveoFieldNameByString } from './Mappers/CoveoFieldMapper';
import { PersonalizedFieldNames } from './Models/CoveoModels';
//import StateFilter from './Components/StateFilter';
import { useUserData } from 'Foundation/User/UserContext';
import { updateSearchAQStateFilters } from './Helpers/UserHelper';
import { NotifyTrigger } from './Components/NotifyTrigger';

interface ISearchPageContentProps {
  queryType: string | undefined;
}

type LazyRef<T> = { current: T };

function useLazyRef<T>(factory: () => T): LazyRef<T> {
  const ref = useRef<T | null>(null);

  if (ref.current === null) {
    ref.current = factory();
  }

  return ref as LazyRef<T>;
}

export default function SearchPageContent({ queryType }: ISearchPageContentProps) {
  const { userData } = useUserData();
  const { headlessEngine } = useCoveoSearchEngine();
  const { logSearchFromLink } = loadSearchAnalyticsActions(headlessEngine!);
  const { executeSearch } = loadSearchActions(headlessEngine!);
  const searchStatus = useLazyRef(() => buildSearchStatus(headlessEngine!));
  const [searchStatusState, setSearchStatusState] = useState(searchStatus.current.state);
  const breadcrumbManager = useLazyRef(() => buildBreadcrumbManager(headlessEngine!));
  const didYouMean = useLazyRef(() => buildDidYouMean(headlessEngine!));
  const querySummary = useLazyRef(() => buildQuerySummary(headlessEngine!));
  const [stateFilter, setStateFilter] = useState(userData?.state ? userData.state.toUpperCase() : '');
  const [defaultProductFacet, setDefaultProductFacet] = useState<string>();

  const onStateSelected = (value?: string) => {
    try {
      if (value?.toUpperCase() !== stateFilter) {
        let newState = value && value !== '' ? value : 'all';
        setStateFilter(newState.toUpperCase());
        updateSearchAQStateFilters(headlessEngine!, newState.toUpperCase());
        headlessEngine!.dispatch(executeSearch(logSearchFromLink()));
        // TODO: update logic to update global state selection without breaking search results state filtering
        // if (newState !== 'all') {
        //   updateSelectedState(newState);
        // }
      }
    } catch {
      console.error("Error saving user's state");
    }
  };
  const notifyController = useLazyRef(() => buildNotifyTrigger(headlessEngine!));
  const relevanceSortCriterion = useLazyRef(() => buildRelevanceSortCriterion());

  const pager = useLazyRef(() =>
    buildPager(headlessEngine!, {
      options: { numberOfPages: 3 },
    }),
  );

  const sort = useLazyRef(() =>
    buildSort(headlessEngine!, {
      initialState: {
        criterion:
          queryType?.toLowerCase() !== 'news'
            ? relevanceSortCriterion.current
            : buildFieldSortCriterion('lmcpublishdate', SortOrder.Descending),
      },
    }),
  );

  const resultsPerPage = useLazyRef(() =>
    buildResultsPerPage(headlessEngine!, {
      initialState: { numberOfResults: 10 },
    }),
  );

  const resultList = useLazyRef(() =>
    buildFoldedResultList(headlessEngine!, {
      options: {
        folding: {
          numberOfFoldedResults: 5,
        },
        fieldsToInclude: [
          'updated',
          'created',
          'date',
          getCoveoFieldNameByString(PersonalizedFieldNames.effectivedate)?.coveoFieldName!,
          getCoveoFieldNameByString(PersonalizedFieldNames.states)?.coveoFieldName!,
          getCoveoFieldNameByString(PersonalizedFieldNames.componentguid)?.coveoFieldName!,
          getCoveoFieldNameByString(PersonalizedFieldNames.contenttype)?.coveoFieldName!,
          getCoveoFieldNameByString(PersonalizedFieldNames.archived)?.coveoFieldName!,
          getCoveoFieldNameByString(PersonalizedFieldNames.usertype)?.coveoFieldName!,
          getCoveoFieldNameByString(PersonalizedFieldNames.headline)?.coveoFieldName!,
          getCoveoFieldNameByString(PersonalizedFieldNames.searchdescription)?.coveoFieldName!,
          getCoveoFieldNameByString(PersonalizedFieldNames.publishdate)?.coveoFieldName!,
        ],
      },
    }),
  );

  const searchBox = useLazyRef(() =>
    buildSearchBox(headlessEngine!, {
      options: {
        enableQuerySyntax: true,
        highlightOptions: {
          notMatchDelimiters: {
            open: '<strong>',
            close: '</strong>',
          },
          correctionDelimiters: {
            open: '<i>',
            close: '</i>',
          },
        },
      },
    }),
  );

  const facetContentType = useLazyRef(() =>
    buildFacet(headlessEngine!, {
      options: {
        facetId: getCoveoFieldNameByString(PersonalizedFieldNames.contenttype)?.coveoFieldName!,
        numberOfValues: 5,
        field: getCoveoFieldNameByString(PersonalizedFieldNames.contenttype)?.coveoFieldName!,
      },
    }),
  );

  const facetProducts = useLazyRef(() =>
    buildFacet(headlessEngine!, {
      options: {
        facetId: getCoveoFieldNameByString(PersonalizedFieldNames.products)?.coveoFieldName!,
        numberOfValues: new URLSearchParams(window.location.search)?.get('lob') ? 12 : 5,
        field: getCoveoFieldNameByString(PersonalizedFieldNames.products)?.coveoFieldName!,
      },
    }),
  );

  useEffect(() => {
    let defaultProduct: string | null = new URLSearchParams(window.location.search)?.get('lob');
    if (defaultProduct) setDefaultProductFacet(defaultProduct);
    const unsub = searchStatus.current.subscribe(() => {
      setSearchStatusState(searchStatus.current.state);
    });
    return function cleanup() {
      unsub();
    };
  }, []);

  return (
    <>
      <div className="search-page">
          <SearchBox searchBox={searchBox} />
          <Box my={1} className="search-page-section">
            <GridRow>
              <GridCol base={3} style={{ minWidth: '190px' }}>
                {/*<StateFilter onChange={onStateSelected} stateValue={stateFilter} show={searchStatusState.hasResults} />*/}

                <Facet
                  title={getCoveoFieldNameByString(PersonalizedFieldNames.contenttype)?.displayName ?? ''}
                  facetType={facetContentType}
                  defaultValue={queryType?.toLowerCase() === 'news' ? 'News Article' : undefined}
                />

                <Facet
                  title={getCoveoFieldNameByString(PersonalizedFieldNames.products)?.displayName ?? ''}
                  facetType={facetProducts}
                  defaultValue={defaultProductFacet}
                />
              </GridCol>
              <GridCol base={9}>
                <GridRow style={{ alignItems: 'flex-end' }}>
                  <NotifyTrigger controller={notifyController.current} />
                </GridRow>
                <GridRow style={{ alignItems: 'flex-end' }}>
                  <div className="search-page-section">
                    <FacetBreadcrumbs
                      breadcrumbManager={breadcrumbManager}
                      stateFilter={stateFilter}
                      onStateFilterChanged={onStateSelected}
                    />
                    <DidYouMean didYouMean={didYouMean} />
                  </div>
                  <div className="search-page-divider">{searchStatusState.hasResults && <span></span>}</div>
                </GridRow>
                <GridRow style={{ alignItems: 'flex-end' }} className="search-page-section">
                  <GridCol base={6}>
                    <QuerySummary querySummary={querySummary} />
                  </GridCol>
                  <GridCol base={6} style={{ textAlign: 'right', alignSelf: 'center' }}>
                    <Sort
                      sort={sort}
                      relevanceSortCriterion={relevanceSortCriterion}
                      show={searchStatusState.hasResults}
                      queryType={queryType}
                    />
                  </GridCol>
                </GridRow>
                <div className="search-page-divider">{searchStatusState.hasResults && <span></span>}</div>
                <ResultTemplatesProvider>
                  <ResultListFolded
                    resultList={resultList}
                    show={searchStatusState.firstSearchExecuted && !searchStatusState.hasResults}
                  />
                </ResultTemplatesProvider>
              </GridCol>
            </GridRow>
          </Box>
          <div className="search-page-divider">{searchStatusState.hasResults && <span></span>}</div>
          <Box my={2}>
            <GridRow>
              <GridCol base={6}>
                <ResultsPerPage resultsPerPage={resultsPerPage} show={searchStatusState.hasResults} />
              </GridCol>
              <GridCol base={6} className="search-page-align-end">
                <Pager pager={pager} />
              </GridCol>
            </GridRow>
          </Box>
        <div>
          {/* The two below components should load after the above ones for making sure they run correctly*/}
          {/*This is a debuger that sends debugging info to Coveo and from Coveo for debug purpose
                    <RelevanceInspector />*/}
        </div>
      </div>
    </>
  );
}
